import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Tabs, Tab, Card, CardContent, IconButton, Chip,
  CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  MenuItem, Switch, FormControlLabel, Stack, Snackbar, Alert, Tooltip, Divider, Paper,
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  AutoStories as DiscoverIcon, MenuBook as JournalIcon,
} from '@mui/icons-material';
import apiClient from '../../services/apiClient';

const KINDS = [
  { value: 'discover', label: 'Discover ideas', icon: <DiscoverIcon fontSize="small" /> },
  { value: 'journal', label: 'Journal topics', icon: <JournalIcon fontSize="small" /> },
];

const CONTENT_TYPES = [
  { value: '', label: '— none —' },
  { value: 'both', label: 'Story / Conversation' },
  { value: 'story', label: 'Story' },
  { value: 'conversation', label: 'Conversation' },
];

const emptyForm = (kind) => ({
  kind,
  category: '',
  title: '',
  body: '',
  description: '',
  translation: '',
  content_type: kind === 'discover' ? 'both' : '',
  tags: '',
  difficulty: '',
  emoji: '',
  sort_order: 0,
  is_active: true,
});

/**
 * LibraryManager — admin CRUD for the shared idea/prompt library that powers the
 * "Browse ideas" (Discover) and "Browse topics" (Journal) modals. Items are
 * grouped by category; tags are edited as a comma-separated string.
 */
const LibraryManager = () => {
  const [kind, setKind] = useState('discover');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm('discover'));

  const [notice, setNotice] = useState({ open: false, message: '', severity: 'success' });
  const notify = (message, severity = 'success') => setNotice({ open: true, message, severity });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.admin.getLibrary(kind);
      setItems(res.data.items || []);
    } catch (e) {
      console.error(e);
      notify('Failed to load library items', 'error');
    } finally {
      setLoading(false);
    }
  }, [kind]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm(kind));
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditingId(item.id);
    setForm({
      kind: item.kind,
      category: item.category || '',
      title: item.title || '',
      body: item.body || '',
      description: item.description || '',
      translation: item.translation || '',
      content_type: item.content_type || '',
      tags: (item.tags || []).join(', '),
      difficulty: item.difficulty || '',
      emoji: item.emoji || '',
      sort_order: item.sort_order || 0,
      is_active: item.is_active !== false,
    });
    setDialogOpen(true);
  };

  const handleField = (field) => (e) => {
    const value = field === 'is_active' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [field]: value }));
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.body.trim() || !form.category.trim()) {
      notify('Title, category, and body are required', 'warning');
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      sort_order: Number(form.sort_order) || 0,
      tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean),
      content_type: form.content_type || null,
      translation: form.translation || null,
    };
    try {
      if (editingId) {
        await apiClient.admin.updateLibraryItem(editingId, payload);
        notify('Item updated');
      } else {
        await apiClient.admin.createLibraryItem(payload);
        notify('Item created');
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      console.error(e);
      notify(e.response?.data?.error || 'Save failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.title}"? This can't be undone.`)) return;
    try {
      await apiClient.admin.deleteLibraryItem(item.id);
      notify('Item deleted');
      load();
    } catch (e) {
      console.error(e);
      notify('Delete failed', 'error');
    }
  };

  // Group items by category for display.
  const grouped = items.reduce((acc, it) => {
    (acc[it.category] = acc[it.category] || []).push(it);
    return acc;
  }, {});

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ sm: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 3, gap: 1 }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>Library</Typography>
          <Typography variant="body2" color="text.secondary">
            Curate the ideas and prompts learners can browse on Discover and in the Journal.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreate}
          sx={{ textTransform: 'none', borderRadius: 2 }}
        >
          Add {kind === 'journal' ? 'topic' : 'idea'}
        </Button>
      </Stack>

      <Paper variant="outlined" sx={{ mb: 3, borderRadius: 3, px: 1 }}>
        <Tabs
          value={kind}
          onChange={(e, v) => setKind(v)}
          textColor="primary"
          TabIndicatorProps={{ sx: { height: 3, borderRadius: 3, bgcolor: 'secondary.main' } }}
        >
          {KINDS.map((k) => (
            <Tab
              key={k.value}
              value={k.value}
              icon={k.icon}
              iconPosition="start"
              label={k.label}
              sx={{ textTransform: 'none', fontWeight: 600, minHeight: 52 }}
            />
          ))}
        </Tabs>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : items.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 5, textAlign: 'center', borderRadius: 3 }}>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            No items yet. Add your first one, or run the seed script.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreate}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            Add {kind === 'journal' ? 'topic' : 'idea'}
          </Button>
        </Paper>
      ) : (
        Object.entries(grouped).map(([category, list]) => (
          <Box key={category} sx={{ mb: 3 }}>
            <Typography variant="overline" sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: 1 }}>
              {category} · {list.length}
            </Typography>
            <Stack spacing={1} sx={{ mt: 0.5 }}>
              {list.map((item) => (
                <Card
                  key={item.id}
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    borderStyle: item.is_active ? 'solid' : 'dashed',
                    borderColor: item.is_active ? 'divider' : 'warning.light',
                    transition: 'box-shadow .15s, border-color .15s',
                    '&:hover': { boxShadow: 2, borderColor: 'primary.main' },
                  }}
                >
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      {item.emoji && <Typography sx={{ fontSize: 22 }}>{item.emoji}</Typography>}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{item.title}</Typography>
                          {!item.is_active && <Chip size="small" label="Hidden" color="warning" />}
                          {item.content_type && <Chip size="small" variant="outlined" label={item.content_type} />}
                          {item.difficulty && <Chip size="small" variant="outlined" label={item.difficulty} />}
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                          {item.body}
                        </Typography>
                        {(item.tags || []).length > 0 && (
                          <Box sx={{ mt: 0.5, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {item.tags.map((t) => <Chip key={t} size="small" label={t} variant="outlined" />)}
                          </Box>
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', flexShrink: 0 }}>
                        <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(item)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleDelete(item)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </Box>
        ))
      )}

      {/* Create / edit dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>{editingId ? 'Edit item' : `Add ${kind === 'journal' ? 'topic' : 'idea'}`}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <TextField select label="Kind" value={form.kind} onChange={handleField('kind')} fullWidth>
              {KINDS.map((k) => <MenuItem key={k.value} value={k.value}>{k.label}</MenuItem>)}
            </TextField>
            <TextField label="Category" value={form.category} onChange={handleField('category')} fullWidth
              helperText="Groups the item in the browser, e.g. Travel Basics, Adventures, Daily Life." />
            <TextField label="Title" value={form.title} onChange={handleField('title')} fullWidth />
            <TextField label={form.kind === 'journal' ? 'Prompt / topic text' : 'Premise (fills the prompt box)'}
              value={form.body} onChange={handleField('body')} fullWidth multiline minRows={3} />
            <TextField label="Card description (short blurb)" value={form.description}
              onChange={handleField('description')} fullWidth multiline minRows={2} />
            {form.kind === 'discover' && (
              <TextField select label="Content type" value={form.content_type} onChange={handleField('content_type')} fullWidth>
                {CONTENT_TYPES.map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
              </TextField>
            )}
            {form.kind === 'journal' && (
              <TextField label="Translation (optional)" value={form.translation}
                onChange={handleField('translation')} fullWidth />
            )}
            <Stack direction="row" spacing={2}>
              <TextField label="Emoji" value={form.emoji} onChange={handleField('emoji')} sx={{ width: 100 }} />
              <TextField label="Difficulty" value={form.difficulty} onChange={handleField('difficulty')}
                sx={{ width: 130 }} helperText="e.g. A1" />
              <TextField label="Sort order" type="number" value={form.sort_order}
                onChange={handleField('sort_order')} sx={{ width: 120 }} />
            </Stack>
            <TextField label="Tags (comma-separated)" value={form.tags} onChange={handleField('tags')} fullWidth />
            <Divider />
            <FormControlLabel
              control={<Switch checked={form.is_active} onChange={handleField('is_active')} />}
              label="Active (visible to learners)"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none', borderRadius: 2 }}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ textTransform: 'none', borderRadius: 2 }}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={notice.open} autoHideDuration={4000}
        onClose={() => setNotice((n) => ({ ...n, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={notice.severity} variant="filled"
          onClose={() => setNotice((n) => ({ ...n, open: false }))}>
          {notice.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LibraryManager;
